import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import JobList from "@/elements/subcomponents/JobList";
import RecentsTable from "@elements/tables/RecentsTable";
import RequestModal from "@modals/RequestModal";
import FieldTranslationModal from "@modals/FieldTranslationModal";
import Helpers from "../Helpers";
import { toast } from "react-toastify";
import { useI18n } from "@/i18n/I18nProvider";
import { getFirstNonEnglishLanguageId } from "@/utils/languageUtils";
import { readPermIdsFromSession } from "@/i18n/sessionUser";
import { fetchCompanyJobsTabMerged } from "@/utils/companyJobsTabApi";

const RECRUIT_TEXT = {
    enterJobs: "Enter Jobs",
    associateJobsToEntity: "Associate Jobs to {entityPlural}",
    defineBehavioralExpectations: "Define Behavioral Expectations",
    createQuestionnaire: "Create Questionnaire",
    viewResults: "View Results",
    jobs: "Jobs",
    reviewed: "Reviewed",
    scheduled: "Scheduled",
    pending: "Pending",
    totalJobs: "Total Jobs",
    missingEntityPlural: "Missing {entityPlural}",
    jobsMissingEntityPlural: "Jobs Missing {entityPlural}",
    jobsWithBEs: "Jobs With BEs",
    missingBEs: "Missing BEs",
    totalMissingBEs: "Total Missing BEs",
    jobsMissingJDs: "Jobs Missing JDs",
    noPermission: "You do not have permission to view this content.",
    jobMatchWorkflow: "Compatibility Workflow",
    step: "Step",
} as const;

function applyTemplate(template: string, values?: Record<string, string | number>) {
    if (!values) return template;
    return Object.entries(values).reduce((next, [key, value]) => {
        return next.replaceAll(`{${key}}`, String(value));
    }, template);
}

function classNames(...classes: Array<string | null | undefined | false>) {
    return classes.filter(Boolean).join(" ");
}

function RecruitRecentsDetail({ company, adminPerms, updateSpinner }) {
    const safeUpdateSpinner =
        typeof updateSpinner === "function" ? updateSpinner : () => { };

    return (
        <RecentsTable
            tabData={{
                company,
                adminPerms: Array.isArray(adminPerms) ? adminPerms : [],
                updateSpinner: safeUpdateSpinner
            }}
        />
    );
}

function RecruitRequestDetail({
    company,
    updateSpinner,
    adminPerms,
    openRequestModalSignal,
    lastHandledRequestModalSignal,
    onRequestModalSignalHandled
}) {
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);

    useEffect(() => {
        if (openRequestModalSignal > lastHandledRequestModalSignal) {
            setIsRequestModalOpen(true);
            if (typeof onRequestModalSignalHandled === "function") {
                onRequestModalSignalHandled(openRequestModalSignal);
            }
        }
    }, [openRequestModalSignal, lastHandledRequestModalSignal, onRequestModalSignalHandled]);

    return (
        <>
            <RequestModal
                isOpen={isRequestModalOpen}
                title="Create Questionnaire"
                onClose={() => setIsRequestModalOpen(false)}
                companyId={company?.id}
                company={company}
                updateSpinner={updateSpinner}
                adminPerms={adminPerms}
                preferredEvalComparison={Enums.TEST_EVAL_COMPARISON.EF}
                includeEvalComparisons={[Enums.TEST_EVAL_COMPARISON.EF]}
            />
        </>
    );
}

const workflowSubcomponents = {
    JobList,
    Request: RecruitRequestDetail,
    RecentsTable: RecruitRecentsDetail
};

const resolveWorkflowElement = (element, fallbackId) => {
    if (typeof element === "function") return element;
    if (typeof element === "string") {
        return workflowSubcomponents[element] ?? null;
    }
    if (fallbackId === "jobs") return JobList;
    if (fallbackId === "interview") return RecruitRequestDetail;
    if (fallbackId === "offer") return RecruitRecentsDetail;
    return null;
};

const defaultWorkflow = (company) => [
    {
        id: "jobs",
        label: "Enter Jobs",
        kpi: 0,
        black: 0,
        red: 0,
        green: 0,
        yellow: 0,
        kpiLabel: "Jobs",
        onClick: "",
        element: "JobList",
        helpId: "jobs"
    },
    {
        id: "bis",
        label: "Define Behavioral Expectations",
        kpi: 0,
        black: 0,
        red: 0,
        green: 0,
        yellow: 0,
        kpiLabel: "Reviewed",
        onClick: "",
        element: "JobList",
        helpId: "bis"
    },
    {
        id: "interview",
        label: "Create Questionnaire",
        kpi: 0,
        black: 0,
        red: 0,
        green: 0,
        yellow: 0,
        kpiLabel: "Scheduled",
        onClick: "",
        element: "Request",
        helpId: "interview"
    },
    {
        id: "offer",
        label: "View Results",
        kpi: 0,
        black: 0,
        red: 0,
        green: 0,
        yellow: 0,
        kpiLabel: "Pending",
        onClick: "",
        element: "RecentsTable",
        helpId: "offer"
    }
];

const RECRUIT_ACTIVE_STEP_STORAGE_KEY = "RecruitActiveStep";

export default function CompanyRecruitTab(props) {
    const { activeTab, company, updateSpinner, adminPerms } = props.tabData;
    const { t, fieldTranslations, languages: supportedLanguages, languageId: currentLanguageId, refreshFieldTranslations } = useI18n();
    const [isFieldTranslationModalOpen, setIsFieldTranslationModalOpen] = useState(false);
    const [selectedFieldTranslation, setSelectedFieldTranslation] = useState(null);
    const [selectedCard, setSelectedCard] = useState(() => {
        try {
            return window.localStorage.getItem(RECRUIT_ACTIVE_STEP_STORAGE_KEY) || null;
        } catch {
            return null;
        }
    });
    const [hoveredKpiHighlight, setHoveredKpiHighlight] = useState(null);
    const [hoveredKpiColorClass, setHoveredKpiColorClass] = useState(null);
    const [recruitDashboard, setRecruitDashboard] = useState(null);
    const [companyJobs, setCompanyJobs] = useState(null);
    const [workflowSeed, setWorkflowSeed] = useState(() => defaultWorkflow(company));
    const [didRunInitialStepFallback, setDidRunInitialStepFallback] = useState(false);
    const [requestModalLaunchCount, setRequestModalLaunchCount] = useState(0);
    const [lastHandledRequestModalSignal, setLastHandledRequestModalSignal] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const isOnThisTab = activeTab && activeTab === "recruit";
    const isSysAdmin = readPermIdsFromSession().includes(Number(Enums.ROLE_PERMS.SystemAdmin));

    function getKpiHighlight(itemId, translationKey, label) {
        if (itemId === "jobs" && translationKey === "recruit.kpi.totalJobs") return "jobTitle";
        if (itemId === "jobs" && translationKey === "recruit.kpi.missingEntityPlural") return "missingLocation";
        if (itemId === "locations" && translationKey === "recruit.kpi.jobsMissingEntityPlural") return "missingLocation";
        if (itemId === "jds" && translationKey === "recruit.kpi.jobsMissingJDs") return "missingJD";
        if (itemId === "bis" && (
            translationKey === "recruit.kpi.missingBEs" ||
            translationKey === "recruit.kpi.totalMissingBEs" ||
            translationKey === "recruit.kpi.missingBIs" ||
            translationKey === "recruit.kpi.totalMissingBIs"
        )) return "missingBEs";

        // Legacy fallback for rows still coming from older API payloads without keys.
        if (itemId === "jobs" && label === "Total Jobs") return "jobTitle";
        if (itemId === "jobs" && (label === "Missing Locations" || label === "Missing Sites")) return "missingLocation";
        if (itemId === "locations" && label === "Jobs Missing Locations") return "missingLocation";
        if (itemId === "jds" && label === "Jobs Missing JDs") return "missingJD";
        if (itemId === "bis" && (
            label === "Missing BEs" ||
            label === "Total Missing BEs" ||
            label === "Missing BIs" ||
            label === "Total Missing BIs"
        )) return "missingBEs";
        return undefined;
    }

    useEffect(() => {
        if (!selectedCard) return;
        try {
            window.localStorage.setItem(RECRUIT_ACTIVE_STEP_STORAGE_KEY, selectedCard);
        } catch {
        }
    }, [selectedCard]);

    function resolveEnglishTextFromFieldKey(fieldKey) {
        const entityPlural = Helpers.pluralize(company?.subEntityTypeName) || "Sites";
        switch (fieldKey) {
            case "recruit.workflow.enterJobs":
                return RECRUIT_TEXT.enterJobs;
            case "recruit.workflow.associateJobsToEntity":
                return applyTemplate(RECRUIT_TEXT.associateJobsToEntity, { entityPlural });
            case "recruit.workflow.defineBehavioralExpectations":
                return RECRUIT_TEXT.defineBehavioralExpectations;
            case "recruit.workflow.createQuestionnaire":
                return RECRUIT_TEXT.createQuestionnaire;
            case "recruit.workflow.viewResults":
                return RECRUIT_TEXT.viewResults;
            case "recruit.kpi.totalJobs":
                return RECRUIT_TEXT.totalJobs;
            case "recruit.kpi.missingEntityPlural":
                return applyTemplate(RECRUIT_TEXT.missingEntityPlural, { entityPlural });
            case "recruit.kpi.jobsMissingEntityPlural":
                return applyTemplate(RECRUIT_TEXT.jobsMissingEntityPlural, { entityPlural });
            case "recruit.kpi.jobsWithBEs":
            case "recruit.kpi.jobsWithBIs":
                return RECRUIT_TEXT.jobsWithBEs;
            case "recruit.kpi.missingBEs":
            case "recruit.kpi.missingBIs":
                return RECRUIT_TEXT.missingBEs;
            case "recruit.kpi.totalMissingBEs":
            case "recruit.kpi.totalMissingBIs":
                return RECRUIT_TEXT.totalMissingBEs;
            case "recruit.kpi.jobsMissingJDs":
                return RECRUIT_TEXT.jobsMissingJDs;
            case "recruit.noPermission":
                return RECRUIT_TEXT.noPermission;
            case "recruit.jobMatchWorkflow":
                return RECRUIT_TEXT.jobMatchWorkflow;
            case "recruit.step":
                return RECRUIT_TEXT.step;
            case "jobList.jobsCaption":
                return "Jobs";
            case "jobList.jobsHeader.title":
                return "Title";
            case "jobList.jobsHeader.all":
                return "All";
            case "jobList.jobsHeader.active":
                return "Active";
            case "jobList.jobsHeader.entityPlural":
                return "{entityPlural}";
            case "jobList.jobsHeader.bes":
            case "jobList.jobsHeader.bis":
                return "BEs";
            case "jobList.jobsHeader.missingBEs":
            case "jobList.jobsHeader.missingBIs":
                return "Missing BEs";
            case "jobList.noJobsCreated":
                return "No jobs have been created";
            case "jobList.jobLocationsCaption":
                return "{jobName} {entityName}";
            case "jobList.jobLocationsHeader.entityName":
                return "{entityName}";
            case "jobList.jobLocationsHeader.access":
                return "Access";
            case "jobList.jobLocationsHeader.bes":
            case "jobList.jobLocationsHeader.bis":
                return "BEs";
            case "jobList.noEntityAvailable":
                return "No {entityPlural} available";
            case "jobList.selectJobToViewAssignments":
                return "Select a job to view {entityName} assignments";
            default:
                return "";
        }
    }

    async function openFieldTranslationEditorByKeyAsync(fieldKey, fallbackText) {
        const englishText = (resolveEnglishTextFromFieldKey(fieldKey) || fallbackText || "").trim();
        if (!englishText) {
            toast.error("Unable to resolve field translation text.");
            return;
        }

        const preferredLanguageId = Number(currentLanguageId) > 1
            ? Number(currentLanguageId)
            : getFirstNonEnglishLanguageId(supportedLanguages);
        const existing = fieldTranslations.find(
            (item) =>
                String(item?.englishText || "").trim() === englishText &&
                Number(item?.languageId || 0) === preferredLanguageId
        );

        setSelectedFieldTranslation(
            existing
                ? {
                    id: existing.id,
                    englishText: existing.englishText,
                    languageId: existing.languageId,
                    translation: existing.translation,
                }
                : {
                    id: 0,
                    englishText,
                    languageId: preferredLanguageId,
                    translation: "",
                }
        );
        setIsFieldTranslationModalOpen(true);
    }

    function handleFieldTranslationCtrlClick(event) {
        if (!isSysAdmin || !event.ctrlKey) return;

        const source = event.target instanceof Element
            ? event.target.closest(".fieldTranslation[data-field-translation-key]")
            : null;
        if (!source) return;

        const fieldKey = source.getAttribute("data-field-translation-key") || "";
        if (!fieldKey) return;

        event.preventDefault();
        event.stopPropagation();

        const fallbackText = (source.textContent || "").trim();
        void openFieldTranslationEditorByKeyAsync(fieldKey, fallbackText);
    }

    async function handleCloseFieldTranslationModal() {
        setIsFieldTranslationModalOpen(false);
        setSelectedFieldTranslation(null);
        await refreshFieldTranslations();
    }

    const workflowItems = useMemo(() => {
        const items = props?.tabData?.recruitWorkflow;
        const seed = Array.isArray(items) && items.length > 0 ? items : workflowSeed;
        return seed.filter((item) => item?.id !== "locations" && item?.helpId !== "locations").map((item) => {
            if (item?.id === "offer") {
                return {
                    ...item,
                    onClick: "",
                    element: RecruitRecentsDetail
                };
            }

            const resolvedElement = resolveWorkflowElement(item?.element, item?.id);

            if (!resolvedElement) {
                if (typeof item?.element === "string") {
                    return { ...item, element: null };
                }
                return item;
            }
            if (resolvedElement === item?.element) return item;
            return { ...item, element: resolvedElement };
        });
    }, [props?.tabData?.recruitWorkflow, workflowSeed]);

    useEffect(() => {
        if (!selectedCard) {
            setSelectedCard(workflowItems[0]?.id ?? null);
            return;
        }

        if (!workflowItems.find((item) => item.id === selectedCard)) {
            setSelectedCard(workflowItems[0]?.id ?? null);
        }
    }, [workflowItems, selectedCard]);

    useEffect(() => {
        if (didRunInitialStepFallback) return;
        if (!Array.isArray(workflowItems) || workflowItems.length === 0) return;

        const currentItem = workflowItems.find((item) => item.id === selectedCard);
        const currentDetailComponent = resolveWorkflowElement(currentItem?.element, currentItem?.id);
        const shouldFallbackToFirst =
            !currentItem ||
            !currentDetailComponent ||
            (selectedCard === "interview" && workflowItems[0]?.id !== "interview");

        if (shouldFallbackToFirst) {
            const firstStepId = workflowItems[0]?.id ?? null;
            setSelectedCard(firstStepId);
            if (firstStepId) {
                try {
                    window.localStorage.setItem(RECRUIT_ACTIVE_STEP_STORAGE_KEY, firstStepId);
                } catch {
                }
            }
        }

        setDidRunInitialStepFallback(true);
    }, [workflowItems, selectedCard, didRunInitialStepFallback]);

    useEffect(() => {
        if (!recruitDashboard) return;

        const jobs = Array.isArray(recruitDashboard.jobs) ? recruitDashboard.jobs : [];
        const activeJobs = jobs.filter((job) => job?.active !== false);
        const totalJobs = activeJobs.length;
        const jobsMissingLocations = activeJobs.filter(
            (job) => !Array.isArray(job?.sites) || job.sites.length === 0
        ).length;
        const jobsWithoutEfsCount = activeJobs.filter((job) => {
            const sites = Array.isArray(job?.sites) ? job.sites : [];
            if (sites.length === 0) return true;
            return !sites.some((site) => Number(site?.efCount ?? 0) > 0);
        }).length;
        const jobsWithEfsCount = Math.max(0, totalJobs - jobsWithoutEfsCount);
        const totalMissingEfs = activeJobs.reduce((count, job) => {
            if (Number(job?.descCount ?? 0) <= 0) return count;
            const sites = Array.isArray(job?.sites) ? job.sites : [];
            const missingForJob = sites.filter((site) => Number(site?.efCount ?? 0) <= 0).length;
            return count + missingForJob;
        }, 0);
        const missingJDs = Number(activeJobs.filter((job) => !job?.descCount).length ?? 0);

        setWorkflowSeed((prev) =>
            prev.map((item) =>
                item.id === "jobs"
                    ? {
                        ...item,
                        elements: [
                            {
                                label: t(RECRUIT_TEXT.totalJobs),
                                fieldTranslationKey: "recruit.kpi.totalJobs",
                                value: totalJobs,
                                class: "kpiGreen"
                            },
                            {
                                label: t(
                                    RECRUIT_TEXT.missingEntityPlural,
                                    RECRUIT_TEXT.missingEntityPlural,
                                    { entityPlural: Helpers.pluralize(company?.subEntityTypeName) || "Sites" }
                                ),
                                fieldTranslationKey: "recruit.kpi.missingEntityPlural",
                                value: jobsMissingLocations,
                                class: "kpiRed"
                            },
                        ]
                    }
                    : item.id === "locations"
                        ? {
                            ...item,
                            elements: [
                                {
                                    label: t(
                                        RECRUIT_TEXT.jobsMissingEntityPlural,
                                        Helpers.pluralize(company?.subEntityTypeName),
                                        { entityPlural: Helpers.pluralize(company?.subEntityTypeName) || "Sites" }
                                    ),
                                    fieldTranslationKey: "recruit.kpi.jobsMissingEntityPlural",
                                    value: jobsMissingLocations,
                                    class: "kpiRed"
                                }
                            ]
                        }
                    : item.id === "bis"
                        ? {
                            ...item,
                            elements: [
                                {
                                    label: t(RECRUIT_TEXT.jobsWithBEs),
                                    fieldTranslationKey: "recruit.kpi.jobsWithBEs",
                                    value: jobsWithEfsCount,
                                    class: "kpiGreen"
                                },
                                {
                                    label: t(RECRUIT_TEXT.missingBEs),
                                    fieldTranslationKey: "recruit.kpi.missingBEs",
                                    value: jobsWithoutEfsCount,
                                    class: "kpiRed"
                                },
                                {
                                    label: t(RECRUIT_TEXT.totalMissingBEs),
                                    fieldTranslationKey: "recruit.kpi.totalMissingBEs",
                                    value: totalMissingEfs,
                                    class: "kpiRed"
                                }

                            ]
                        } : item.id === "jds"
                            ? {
                                ...item,
                                elements: [
                                    {
                                        label: t(RECRUIT_TEXT.jobsMissingJDs),
                                        fieldTranslationKey: "recruit.kpi.jobsMissingJDs",
                                        value: missingJDs,
                                        class: "kpiRed"
                                    }

                                ]
                            }

                        : item
            )
        );
    }, [recruitDashboard, currentLanguageId, fieldTranslations, company?.subEntityTypeName]);

    useEffect(() => {
        let isMounted = true;

        async function loadRecruitDashboard() {
            if (!company?.id || !isOnThisTab) return;

            const resp = await RightTimeApi.getAsync(
                `/Company/${company.id}/RecruitDashboard`
            );

            if (isMounted) {
                setRecruitDashboard(resp?.jsonData ?? null);
                const apiWorkflow =
                    resp?.jsonData?.workflowItems ||
                    resp?.jsonData?.workflow ||
                    resp?.jsonData?.items;

                if (Array.isArray(apiWorkflow) && apiWorkflow.length > 0) {
                    setWorkflowSeed(apiWorkflow);
                }
            }
        }

        loadRecruitDashboard();

        return () => {
            isMounted = false;
        };
    }, [company?.id, isOnThisTab]);

    async function refreshRecruitDashboard() {
        if (!company?.id || !isOnThisTab) return null;

        const resp = await RightTimeApi.getAsync(
            `/Company/${company.id}/RecruitDashboard`
        );

        setRecruitDashboard(resp?.jsonData ?? null);
        const apiWorkflow =
            resp?.jsonData?.workflowItems ||
            resp?.jsonData?.workflow ||
            resp?.jsonData?.items;

        if (Array.isArray(apiWorkflow) && apiWorkflow.length > 0) {
            setWorkflowSeed(apiWorkflow);
        }

        return resp?.jsonData ?? null;
    }

    async function refreshCompanyJobs() {
        if (!company?.id || !isOnThisTab) return null;

        if (typeof updateSpinner === "function") {
            updateSpinner(true);
        }

        const resp = await fetchCompanyJobsTabMerged(company.id);

        if (typeof updateSpinner === "function") {
            updateSpinner(false);
        }

        if (resp?.jsonData) {
            setCompanyJobs(resp.jsonData);
            return resp.jsonData;
        }

        return null;
    }

    useEffect(() => {
        let isMounted = true;

        async function loadCompanyJobs() {
            if (!company?.id || !isOnThisTab) return;

            if (typeof updateSpinner === "function") {
                updateSpinner(true);
            }

            const resp = await fetchCompanyJobsTabMerged(company.id);

            if (typeof updateSpinner === "function") {
                updateSpinner(false);
            }

            if (isMounted) {
                setCompanyJobs(resp?.jsonData ?? null);
            }
        }

        loadCompanyJobs();

        return () => {
            isMounted = false;
        };
    }, [company?.id, isOnThisTab, updateSpinner]);

    useEffect(() => {
        if (!company?.id || !isOnThisTab) return;

        let isMounted = true;

        const refreshData = async () => {
            const [dashboardResp, jobsResp] = await Promise.all([
                RightTimeApi.getAsync(`/Company/${company.id}/RecruitDashboard`),
                fetchCompanyJobsTabMerged(company.id),
            ]);

            if (!isMounted) return;

            if (dashboardResp?.ok) {
                setRecruitDashboard(dashboardResp?.jsonData ?? null);
                const apiWorkflow =
                    dashboardResp?.jsonData?.workflowItems ||
                    dashboardResp?.jsonData?.workflow ||
                    dashboardResp?.jsonData?.items;

                if (Array.isArray(apiWorkflow) && apiWorkflow.length > 0) {
                    setWorkflowSeed(apiWorkflow);
                }
            }

            if (jobsResp?.ok) {
                setCompanyJobs(jobsResp?.jsonData ?? null);
            }
        };

        refreshData();

        return () => {
            isMounted = false;
        };
    }, [company?.id, isOnThisTab]);

    if (!isOnThisTab) return null;

    const hasPermission =
        company?.permissions?.some(
            (p) =>
                p.companyId === company.id &&
                p.permissionId === Enums.ROLE_PERMS.CompanyMenuCompatibility
        ) ||
        company?.permissions?.some(
            (p) =>
                p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin
        );

    if (!hasPermission) {
        return (
            <div className="companyRecruitTab">
                <div className="tableWrapper factTable">
                    <div
                        className="noData fieldTranslation"
                        data-field-translation-key="recruit.noPermission"
                    >
                        {t(RECRUIT_TEXT.noPermission)}
                    </div>
                </div>
            </div>
        );
    }

    const selectedItem =
        workflowItems.find((item) => item.id === selectedCard) || workflowItems[0];
    const DetailComponent = resolveWorkflowElement(selectedItem?.element, selectedItem?.id);
    const localizedWorkflowItems = workflowItems.map((item) => {
        const entityPlural = Helpers.pluralize(company?.subEntityTypeName) || "Sites";
        const normalizedId = String(item?.id || "").trim().toLowerCase();
        const normalizedHelpId = String(item?.helpId || "").trim().toLowerCase();
        const labelText = String(item?.label || "").trim();

        if (normalizedId === "jobs" || normalizedHelpId === "jobs") {
            return {
                ...item,
                label: t(RECRUIT_TEXT.enterJobs),
                kpiLabel: t(RECRUIT_TEXT.jobs),
                fieldTranslationKey: "recruit.workflow.enterJobs",
            };
        }
        if (
            normalizedId === "locations" ||
            normalizedHelpId === "locations" ||
            /associate\s+jobs/i.test(labelText)
        ) {
            return {
                ...item,
                label: t(RECRUIT_TEXT.associateJobsToEntity, RECRUIT_TEXT.associateJobsToEntity, { entityPlural }),
                kpiLabel: t(RECRUIT_TEXT.reviewed),
                fieldTranslationKey: "recruit.workflow.associateJobsToEntity",
            };
        }
        if (normalizedId === "bis" || normalizedHelpId === "bis") {
            return {
                ...item,
                label: t(RECRUIT_TEXT.defineBehavioralExpectations),
                kpiLabel: t(RECRUIT_TEXT.reviewed),
                fieldTranslationKey: "recruit.workflow.defineBehavioralExpectations",
            };
        }
        if (normalizedId === "interview" || normalizedHelpId === "interview") {
            return {
                ...item,
                label: t(RECRUIT_TEXT.createQuestionnaire),
                kpiLabel: t(RECRUIT_TEXT.scheduled),
                fieldTranslationKey: "recruit.workflow.createQuestionnaire",
            };
        }
        if (normalizedId === "offer" || normalizedHelpId === "offer") {
            return {
                ...item,
                label: t(RECRUIT_TEXT.viewResults),
                kpiLabel: t(RECRUIT_TEXT.pending),
                fieldTranslationKey: "recruit.workflow.viewResults",
            };
        }
        return item;
    });

    function handleHelpHover(event) {
        const source = event.target instanceof Element ? event.target : null;
        const kpiTarget = source
            ? source.closest("[data-kpi-highlight]")
            : null;
        const nextKpiHighlight = kpiTarget?.getAttribute("data-kpi-highlight") || null;
        const nextKpiColorClass = kpiTarget?.getAttribute("data-kpi-color") || null;
        setHoveredKpiHighlight(nextKpiHighlight);
        setHoveredKpiColorClass(nextKpiColorClass);
    }

    function handleItemClick(item) {
        setSelectedCard(item.id);
        if (item?.id === "interview") {
            setRequestModalLaunchCount((prev) => prev + 1);
            if (typeof props?.tabData?.onRecruitDrill === "function") {
                props.tabData.onRecruitDrill(item);
            }
            return;
        }
        if (item?.id === "offer") {
            if (typeof props?.tabData?.onRecruitDrill === "function") {
                props.tabData.onRecruitDrill(item);
            }
            return;
        }
        const rawOnClick = typeof item?.onClick === "string" ? item.onClick.trim() : item?.onClick;
        if (rawOnClick) {
            const sanitized = rawOnClick.replace(/^\/+/, "");
            const nextSearch = sanitized.startsWith("?")
                ? sanitized
                : `?${sanitized}`;
            const searchParams = new URLSearchParams(
                sanitized.startsWith("?") ? sanitized.slice(1) : sanitized
            );
            const nextTab = searchParams.get("tab");
            if (nextTab && typeof props?.tabData?.updateActiveTab === "function") {
                props.tabData.updateActiveTab(nextTab);
            }
            navigate(`${location.pathname}${nextSearch}${location.hash || ""}`);
        }
        if (typeof props?.tabData?.onRecruitDrill === "function") {
            props.tabData.onRecruitDrill(item);
        }
    }

    return (
        <>
        <div
            className="companyRecruitTab"
            onClickCapture={handleFieldTranslationCtrlClick}
            onMouseOver={handleHelpHover}
            onMouseLeave={() => {
                setHoveredKpiHighlight(null);
                setHoveredKpiColorClass(null);
            }}
        >
            <div className="recruitWrapper">
                <div className="recruitLeftSpacer" aria-hidden="true"></div>
                <div className="cultureWorkflow">
                <div className="workflowHeader">
                    <div className="workflowHeaderRow">
                        <div>
                            <div className="workflowTitle fieldTranslation" data-field-translation-key="recruit.jobMatchWorkflow">
                                {t(RECRUIT_TEXT.jobMatchWorkflow)}
                            </div>
                            {/* <div className="workflowSubtitle">Select a stage to drill in.</div> */}
                        </div>
                    </div>
                </div>
                <div className="workflowTimeline" role="list">
                    <div className="workflowTrack" aria-hidden="true"></div>
                    <div className="workflowItems">
                        {localizedWorkflowItems.map((item, index) => (
                            <button
                                key={item.id}
                                type="button"
                                role="listitem"
                                data-help-id={item.helpId}
                                data-workflow-id={item.id}
                                className={
                                    item.id === selectedCard ? "workflowNode isActive" : "workflowNode"
                                }
                                aria-current={item.id === selectedCard ? "step" : undefined}
                                onClick={() => handleItemClick(item)}
                            >
                                <div className="workflowStep">
                                    <span className="fieldTranslation" data-field-translation-key="recruit.step">
                                        {t(RECRUIT_TEXT.step)}
                                    </span>
                                    {" "}{index + 1}
                                </div>
                                <div
                                    className={classNames("workflowLabel", "fieldTranslation")}
                                    data-field-translation-key={item.fieldTranslationKey || `recruit.workflow.${item.id || index}`}
                                >
                                    {item.label}
                                </div>
                                <div className="workflowKpi">
                                    {Array.isArray(item.elements) && item.elements.length > 0
                                        ? item.elements.map((element) => (
                                            <div
                                                key={`${item.id}-${element.label}`}
                                                className={`kpiRow ${element.class || "kpiBlack"}`}
                                            >
                                                <span
                                                    className={classNames("kpiLabel", "fieldTranslation")}
                                                    data-field-translation-key={element.fieldTranslationKey || `recruit.kpi.${item.id}.${element.label || "label"}`}
                                                    data-kpi-color={element.class || "kpiBlack"}
                                                    data-kpi-highlight={getKpiHighlight(item.id, element.fieldTranslationKey, element.label)}
                                                >
                                                    {element.label}:
                                                </span>
                                                <span
                                                    className="kpiValue"
                                                    data-kpi-color={element.class || "kpiBlack"}
                                                    data-kpi-highlight={getKpiHighlight(item.id, element.fieldTranslationKey, element.label)}
                                                >
                                                    {element.value}
                                                </span>
                                            </div>
                                        ))
                                        : (
                                            <>
                                                {item.black > 0 && <span className="kpiBlack"> {item.black}</span>}
                                                {item.red > 0 && <span className="kpiRed"> ({item.red})</span>}
                                                {item.green > 0 && <span className="kpiGreen"> ({item.green})</span>}
                                                {item.yellow > 0 && <span className="kpiYellow"> ({item.yellow})</span>}
                                            </>
                                        )}
                                </div>
                                {/* <div className="workflowKpiLabel">{item.kpiLabel || "KPI"}</div> */}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="workflowDetails">
                    {selectedItem
                        ? DetailComponent
                            ? (
                                <DetailComponent
                                    company={company}
                                    recruitDashboard={recruitDashboard}
                                    companyJobs={companyJobs}
                                    adminPerms={adminPerms}
                                    openRequestModalSignal={requestModalLaunchCount}
                                    lastHandledRequestModalSignal={lastHandledRequestModalSignal}
                                    onRequestModalSignalHandled={setLastHandledRequestModalSignal}
                                    updateSpinner={updateSpinner}
                                    refreshCompanyJobs={refreshCompanyJobs}
                                    refreshRecruitDashboard={refreshRecruitDashboard}
                                    workflowItem={selectedItem}
                                    highlightJobTitleCells={hoveredKpiHighlight === "jobTitle"}
                                    highlightMissingLocationRows={hoveredKpiHighlight === "missingLocation"}
                                    highlightMissingJdRows={hoveredKpiHighlight === "missingJD"}
                                    highlightMissingEfsRows={
                                        hoveredKpiHighlight === "missingBEs" ||
                                        hoveredKpiHighlight === "missingEFs" ||
                                        hoveredKpiHighlight === "missingBEs"
                                    }
                                    highlightKpiColorClass={hoveredKpiColorClass}
                                />
                            )
                            : null
                        : null}
                </div>
                </div>
            </div>
        </div>
        <FieldTranslationModal
            isOpen={isFieldTranslationModalOpen}
            onClose={handleCloseFieldTranslationModal}
            translation={selectedFieldTranslation}
            languages={supportedLanguages}
        />
        </>
    );
}

